import type { ReactNode } from 'react';
import { Undo2, RefreshCw, Lightbulb, Layers } from 'lucide-react';
import { useGameStore } from '../state/game-store.js';
import { useUiStore } from '../state/ui-store.js';
import { useT } from '../i18n/useT.js';
import { HEATMAP_MODE_I18N_KEYS } from '../i18n/heatmapModeKeys.js';
import { Icon } from '@strategy4kids/ui';

/**
 * Sidebar wrapper that switches between the full {@link LeftSidebar} panel
 * and the slim {@link CollapsedSidebar} icon strip based on
 * `ui-store.sidebarCollapsed`. Width animates so the board grows smoothly
 * as the panel collapses.
 *
 * Renders on the LEFT of the board. The screen layouts put this component
 * before the board section so flex order is board-after-sidebar.
 */
export function Sidebar({
  online,
  children,
}: {
  /** Switches between in-game actions (chess/checkers) and online-game actions. */
  readonly online?: boolean;
  readonly children: ReactNode;
}) {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);

  return (
    <aside
      data-testid="sidebar"
      data-appdom-id="sidebar"
      data-appdom-type="Sidebar"
      data-sidebar-collapsed={collapsed ? 'true' : 'false'}
      className={`flex shrink-0 flex-col gap-3 transition-[width] duration-200 ${collapsed ? 'w-12' : 'w-full lg:w-80'}`}
    >
      {collapsed ? (
        <CollapsedSidebar online={online ?? false} onExpand={toggle} />
      ) : (
        <>
          <div className="flex justify-end">
            <button
              type="button"
              data-testid="sidebar-collapse"
              data-appdom-id="sidebar-collapse"
              data-appdom-type="Button"
              aria-label="Collapse sidebar"
              onClick={toggle}
              className="inline-flex h-12 w-12 items-center justify-center rounded-lg border-2 border-sun/40 bg-cream text-ink transition-colors hover:bg-sun/30 max-sm:hidden dark:border-sun/30"
            >
              <Icon name="ChevronRight" size="sm" className="rotate-180" />
            </button>
          </div>
          {children}
        </>
      )}
    </aside>
  );
}

/**
 * Slim ~48 px icon strip — every control maps to an action in the expanded
 * panel so the player never loses capability by collapsing. Order matches
 * the visual order of the same actions in {@link Hud} so the muscle memory
 * reads straight across the collapse.
 */
function CollapsedSidebar({ online, onExpand }: { readonly online: boolean; readonly onExpand: () => void }) {
  const heatmap = useGameStore((s) => s.heatmap);
  const cycleHeatmap = useGameStore((s) => s.cycleHeatmap);
  // The icon strip has no visible label, so the accessible name is all it has.
  // It used to be a second, hand-formatted string built from the raw mode value
  // — one that would have rendered a new mode uncapitalised and untranslated.
  const { t: tGames } = useT('games');
  const heatmapLabel = tGames(HEATMAP_MODE_I18N_KEYS[heatmap]);
  const undo = useGameStore((s) => s.undo);
  const restart = useGameStore((s) => s.restart);
  const askCoach = useGameStore((s) => s.askCoach);
  const phase = useGameStore((s) => s.phase);
  const disabled = phase !== 'playing';

  return (
    <div className="flex flex-col items-center gap-2 rounded-3xl border-2 border-sun/30 bg-cream p-2 shadow-lg dark:border-sun/20">
      <IconButton testid="collapsed-heatmap" label={heatmapLabel} onClick={cycleHeatmap} disabled={disabled}>
        <Layers size={18} />
      </IconButton>
      {!online && (
        <IconButton testid="collapsed-coach" label="Ask coach" onClick={() => void askCoach()} disabled={disabled}>
          <Lightbulb size={18} />
        </IconButton>
      )}
      <IconButton testid="collapsed-undo" label="Undo" onClick={undo} disabled={disabled}>
        <Undo2 size={18} />
      </IconButton>
      <IconButton testid="collapsed-restart" label="Restart" onClick={restart} disabled={disabled}>
        <RefreshCw size={18} />
      </IconButton>
      <div className="mt-1 h-px w-6 bg-sun/30" />
      <IconButton testid="collapsed-expand" label="Expand sidebar" onClick={onExpand}>
        <Icon name="ChevronRight" size="sm" />
      </IconButton>
    </div>
  );
}

function IconButton({
  testid,
  label,
  onClick,
  disabled = false,
  children,
}: {
  readonly testid: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      data-appdom-id={testid}
      data-appdom-type="Button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-12 w-12 items-center justify-center rounded-xl text-ink transition-colors hover:bg-sun/30 disabled:opacity-30"
    >
      {children}
    </button>
  );
}
